import React, { useState, useCallback } from 'react';
import { MenuItem } from '@blueprintjs/core';
import { MultiSelect as BaseMultiSelect } from '@blueprintjs/select';

// Type imports
import type { PicklistOption } from '../types';

import "./Common.css";

/**
 * Props interface for the MultiSelect component
 * Used for multi-select picklist functionality in forms
 */
export interface MultiSelectProps {
  /** Unique identifier for the component */
  id: string;
  /** Available options to select from */
  options: PicklistOption[];
  /** Currently selected values array */
  value?: (string | number)[];
  /** Callback when selection changes */
  onUpdate: (selectedValues: (string | number)[]) => void;
  /** UI label for placeholder text */
  uiLabel?: string;
  /** Text shown while nothing is selected; defaults to "Select <uiLabel>". */
  placeholder?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /**
   * When provided, the dropdown lets the user maintain the option list itself:
   * typing a value that does not exist offers to add it, and with onRename and
   * onDelete each option also carries small rename/delete controls. The
   * callbacks own persistence (and any authorization) - the component only
   * supplies the interaction.
   */
  optionTools?: {
    onRename?: (value: string | number, nextLabel: string) => void | Promise<void>;
    onDelete?: (value: string | number) => void | Promise<void>;
    onAdd: (label: string) => void | Promise<void>;
  };
}

/** Marker item produced by createNewItemFromQuery for the add-option flow. */
interface NewOptionItem extends PicklistOption {
  __isNew?: boolean;
}

/**
 * Multi-select component using Blueprint.js MultiSelect
 * Supports searching, tagging, and removal of selected items
 *
 * TODO: Make tags draggable as noted in original component
 */
const MultiSelect: React.FC<MultiSelectProps> = ({
  id,
  disabled,
  options,
  value,
  onUpdate,
  uiLabel,
  placeholder,
  optionTools,
}) => {
  const [query, setQuery] = useState<string>('');
  // The option currently being renamed inline, and its in-progress text.
  const [editingValue, setEditingValue] = useState<string | number | null>(null);
  const [editingText, setEditingText] = useState('');

  // Handle removal of selected items
  const onRemove = useCallback((item: PicklistOption) => {
    const updatedValues = (value || []).filter((v) => v !== item.value);
    onUpdate(updatedValues);
  }, [value, onUpdate]);

  // Type-safe value array with fallback
  const currentValue = value || [];

  const commitRename = useCallback(() => {
    const next = editingText.trim();
    const original = editingValue;
    setEditingValue(null);
    if (original == null || !next || next === String(original)) return;
    void optionTools?.onRename?.(original, next);
  }, [editingText, editingValue, optionTools]);

  return (
    <BaseMultiSelect
      query={query}
      onQueryChange={setQuery}
      disabled={disabled}
      items={options || []}
      onRemove={onRemove}
      itemRenderer={(item: PicklistOption, { handleClick, modifiers }) => {
        if (!modifiers.matchesPredicate) {
          return null;
        }
        if (optionTools?.onRename && editingValue === item.value) {
          // Inline rename: the row becomes an input until Enter or blur.
          return (
            <li key={item.value} className="sky-multiselect-edit-row">
              <input
                className="sky-field-input"
                autoFocus
                value={editingText}
                aria-label={`Rename ${item.label || String(item.value)}`}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setEditingText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename();
                  if (e.key === 'Escape') setEditingValue(null);
                }}
                onBlur={commitRename}
              />
            </li>
          );
        }
        return (
          <MenuItem
            key={item.value}
            text={item.label || String(item.value)}
            onClick={handleClick}
            active={modifiers.active}
            icon={currentValue.includes(item.value) ? 'tick' : 'blank'}
            shouldDismissPopover={false}
            labelElement={optionTools?.onRename || optionTools?.onDelete ? (
              <span className="sky-multiselect-option-tools">
                {optionTools.onRename && <button
                  type="button"
                  className="sky-icon-btn"
                  title="Rename option"
                  aria-label={`Rename ${item.label || String(item.value)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingText(item.label || String(item.value));
                    setEditingValue(item.value);
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                </button>}
                {optionTools.onDelete && <button
                  type="button"
                  className="sky-icon-btn"
                  title="Delete option"
                  aria-label={`Delete ${item.label || String(item.value)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    void optionTools.onDelete?.(item.value);
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                </button>}
              </span>
            ) : undefined}
          />
        );
      }}
      createNewItemFromQuery={optionTools ? ((q: string): NewOptionItem => ({
        value: q.trim(),
        label: q.trim(),
        __isNew: true,
      })) : undefined}
      createNewItemRenderer={optionTools ? ((q: string, active: boolean, handleClick: React.MouseEventHandler<HTMLElement>) => (
        <MenuItem
          icon="add"
          text={`Add "${q.trim()}"`}
          active={active}
          onClick={handleClick}
          shouldDismissPopover={false}
        />
      )) : undefined}
      tagInputProps={{
        disabled: disabled,
        inputProps: {
          id,
          'aria-label': uiLabel ?? 'Select options',
        },
      }}
      tagRenderer={(item: PicklistOption) => item.label || String(item.value)}
      // A stored value with no matching option (renamed or deleted since the
      // record was saved) still renders as a removable tag rather than
      // silently vanishing from the field.
      selectedItems={currentValue.map((v) => (options || []).find((o) => o.value === v) ?? { value: v, label: String(v) })}
      onItemSelect={(item: NewOptionItem) => {
        if (item.__isNew) {
          const label = String(item.value).trim();
          if (label) void optionTools?.onAdd(label);
          setQuery('');
          return;
        }
        const selectedValues = currentValue;
        let newSelectedValues: (string | number)[];

        if (selectedValues.includes(item.value)) {
          // Remove item if already selected
          newSelectedValues = selectedValues.filter((v) => v !== item.value);
        } else {
          // Add item if not selected
          newSelectedValues = [...selectedValues, item.value];
        }

        onUpdate(newSelectedValues);
        setQuery('');
      }}
      itemPredicate={(query: string, item: PicklistOption) => {
        const label = (item.label || String(item.value)).toLowerCase();
        return label.includes(query.toLowerCase());
      }}
      placeholder={placeholder ?? (uiLabel ? `Select ${uiLabel}` : 'Select options...')}
      fill
      openOnKeyDown={false}
      popoverProps={{ portalClassName: 'sky-elevated-portal' }}
    />
  );
};

export default MultiSelect;
